import { Inject, Injectable, OnDestroy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { AccountGroupSdkService, ProjectionFilterInterface } from '@vendasta/account-group';
import { GalaxyAddressPipe } from '@vendasta/galaxy/pipes';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { Package, PackageService, Status } from '@vendasta/marketplace-packages';
import { ListTagsResponse, SalesOrdersService } from '@vendasta/sales-orders';
import {
  CheckboxFilterControl,
  DateFilterControl,
  FilterGroup,
  SearchSelectFilterControl,
  SelectFilterControl,
  TextInputFilterControl,
} from '@vendasta/va-filter2';
import { BehaviorSubject, Observable, Subject, combineLatest, throwError } from 'rxjs';
import { catchError, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { BaseSalesOrdersTableFilterService, ForcedFilterOptions, Market, Salesperson, Salesteam } from './interface';

import {
  MARKET_IDS_TOKEN,
  PARTNER_ID_TOKEN,
  SALESPEOPLE_TOKEN,
  SALES_TEAMS_TOKEN,
} from '../external-dependencies/provider-tokens';

@Injectable()
export class SalesOrdersTableFilterService extends BaseSalesOrdersTableFilterService implements OnDestroy {
  filters: FilterGroup = new FilterGroup('sales-orders');
  private forcedFilterOptions: ForcedFilterOptions;
  private destroy$ = new Subject<void>();
  marketFilterControl: SelectFilterControl;
  packageFilterSectionName = this.translateService.instant('FRONTEND.SALES_UI.PACKAGES');

  constructor(
    @Inject(PARTNER_ID_TOKEN) readonly partnerId$: Observable<string>,
    @Inject(MARKET_IDS_TOKEN) private readonly markets$: Observable<Market[]>,
    @Inject(SALESPEOPLE_TOKEN) private readonly salespeople$: Observable<Salesperson[]>,
    @Inject(SALES_TEAMS_TOKEN) private readonly salesTeams$: Observable<Salesteam[]>,
    private readonly alertService: SnackbarService,
    private readonly salesOrdersService: SalesOrdersService,
    private translateService: TranslateService,
    private packageService: PackageService,
    private accountGroupService: AccountGroupSdkService,
    private addressPipe: GalaxyAddressPipe,
  ) {
    super();
  }

  private static reduceSalesTeamToMap(salesTeamMap: Map<string, Salesteam>, st: Salesteam): Map<string, Salesteam> {
    salesTeamMap.set(st.teamName, st);
    return salesTeamMap;
  }

  private static convertSalesTeamsToMap(salesTeams: Salesteam[]): Map<string, Salesteam> {
    if (!salesTeams) {
      return new Map<string, Salesteam>();
    }

    return salesTeams.reduce(SalesOrdersTableFilterService.reduceSalesTeamToMap, new Map<string, Salesteam>());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initialize(forcedFilterOptions: ForcedFilterOptions, canAccessSalesTeams: boolean): void {
    this.forcedFilterOptions = forcedFilterOptions;
    const marketOptions$: Observable<Map<string, Market>> = this.markets$.pipe(
      map((markets) => {
        markets.sort((a, b) => {
          const aName = a.name.toLowerCase(),
            bName = b.name.toLowerCase();
          return aName < bName ? -1 : aName > bName ? 1 : 0;
        });
        return markets;
      }),
      map((markets) => {
        return markets.reduce((objMap, market) => {
          objMap.set(market.name, market);
          return objMap;
        }, new Map<string, Market>());
      }),
      catchError((e) => {
        this.alertService.openErrorSnack('FRONTEND.SALES_UI.ERROR_LOADING_MARKETS');
        return throwError(e);
      }),
    );

    const tags: Observable<Map<string, string>> = combineLatest([this.partnerId$, this.markets$]).pipe(
      switchMap(([partnerId, markets]) => {
        const marketIds = this.getMarketIds(markets);
        return this.salesOrdersService.listTags(partnerId, { marketIds: marketIds });
      }),
      map((resp: ListTagsResponse) => {
        const t = new Map<string, string>();
        if (resp.tags) {
          resp.tags.map((v) => t.set(v, v));
        } else {
          const msg = this.translateService.instant('FRONTEND.SALES_UI.NO_TAGS_FOUND');
          t.set(msg, msg);
        }
        return t;
      }),
    );

    const adminControls = [
      new TextInputFilterControl('orderId', this.translateService.instant('FRONTEND.SALES_UI.ORDER_ID')),
    ];

    this.marketFilterControl = new SelectFilterControl(
      'market',
      this.translateService.instant('FRONTEND.SALES_UI.MARKET'),
      marketOptions$,
      null,
      {
        appliedValueMapper: (name, value) => {
          const marketLabel = this.translateService.instant('FRONTEND.SALES_UI.MARKET');
          return { name, label: `${marketLabel}: ${value.name}` };
        },
      },
    );
    if (!this.forcedFilterOptions.marketIds) {
      adminControls.push(this.marketFilterControl);
      // auto-select first market if only one is available
      marketOptions$.pipe(takeUntil(this.destroy$)).subscribe((markets) => {
        const availMarkets: Market[] = markets ? Array.from(markets.values()) : [];
        if (availMarkets.length === 1) {
          this.marketFilterControl?.setValue(availMarkets[0]);
        }
      });
    }

    if (!this.forcedFilterOptions.salespersonId) {
      adminControls.push(this.createSalesPeopleSelectControl());
      if (canAccessSalesTeams) {
        // Enable salesteams filter only if sales teams is enabled for partner's subscription tier
        adminControls.push(this.createSalesTeamsSelectControl());
      }
    }

    adminControls.push(this.createAccountSearchControl());

    if (adminControls.length > 0) {
      this.filters.addSection(this.translateService.instant('FRONTEND.SALES_UI.ADMIN'), adminControls);
    }

    const contractStartFromControl = new DateFilterControl(
      'contractStartFrom',
      this.translateService.instant('FRONTEND.SALES_UI.FROM'),
    );
    const contractStartToControl = new DateFilterControl(
      'contractStartToControl',
      this.translateService.instant('FRONTEND.SALES_UI.TO'),
    );
    const submittedDateFromControl = new DateFilterControl(
      'submittedDateFromControl',
      this.translateService.instant('FRONTEND.SALES_UI.FROM'),
    );
    const submittedDateToControl = new DateFilterControl(
      'submittedDateToControl',
      this.translateService.instant('FRONTEND.SALES_UI.TO'),
    );
    const expiryDateFromControl = new DateFilterControl(
      'expiryDateFrom',
      this.translateService.instant('FRONTEND.SALES_UI.FROM'),
    );
    const expiryDateToControl = new DateFilterControl(
      'expiryDateTo',
      this.translateService.instant('FRONTEND.SALES_UI.TO'),
    );

    this.filters
      .addSection(this.translateService.instant('FRONTEND.SALES_UI.STATUS'), [
        new CheckboxFilterControl(
          'resubmitted',
          this.translateService.instant('FRONTEND.SALES_UI.RESUBMITTED'),
          false,
          {
            appliedValueMapper: (name) => ({
              name,
              label: this.translateService.instant('FRONTEND.SALES_UI.RESUBMITTED'),
            }),
          },
        ),
        new CheckboxFilterControl('pending', this.translateService.instant('FRONTEND.SALES_UI.PENDING'), false, {
          appliedValueMapper: (name) => ({
            name,
            label: this.translateService.instant('FRONTEND.SALES_UI.PENDING'),
          }),
        }),
        new CheckboxFilterControl(
          'awaiting-customer-approval',
          this.translateService.instant('FRONTEND.SALES_UI.AWAITING_CUSTOMER_APPROVAL'),
          false,
          {
            appliedValueMapper: (name) => ({
              name,
              label: this.translateService.instant('FRONTEND.SALES_UI.AWAITING_CUSTOMER_APPROVAL'),
            }),
          },
        ),
        new CheckboxFilterControl('approved', this.translateService.instant('FRONTEND.SALES_UI.APPROVED'), false, {
          appliedValueMapper: (name) => ({
            name,
            label: this.translateService.instant('FRONTEND.SALES_UI.APPROVED'),
          }),
        }),
        new CheckboxFilterControl('processing', this.translateService.instant('FRONTEND.SALES_UI.PROCESSING'), false, {
          appliedValueMapper: (name) => ({
            name,
            label: this.translateService.instant('FRONTEND.SALES_UI.PROCESSING'),
          }),
        }),
        new CheckboxFilterControl(
          'activation-errors',
          this.translateService.instant('FRONTEND.SALES_UI.ACTIVATION_ERRORS'),
          false,
          {
            appliedValueMapper: (name) => ({
              name,
              label: this.translateService.instant('FRONTEND.SALES_UI.ACTIVATION_ERRORS'),
            }),
          },
        ),
        new CheckboxFilterControl('archived', this.translateService.instant('FRONTEND.SALES_UI.ARCHIVED'), false, {
          appliedValueMapper: (name) => ({
            name,
            label: this.translateService.instant('FRONTEND.SALES_UI.ARCHIVED'),
          }),
        }),
        new CheckboxFilterControl('activated', this.translateService.instant('FRONTEND.SALES_UI.ACTIVATED'), false, {
          appliedValueMapper: (name) => ({
            name,
            label: this.translateService.instant('FRONTEND.SALES_UI.ACTIVATED'),
          }),
        }),
        new CheckboxFilterControl('declined', this.translateService.instant('FRONTEND.SALES_UI.DECLINED'), false, {
          appliedValueMapper: (name) => ({
            name,
            label: this.translateService.instant('FRONTEND.SALES_UI.DECLINED'),
          }),
        }),
        new CheckboxFilterControl(
          'scheduled-activation',
          this.translateService.instant('FRONTEND.SALES_UI.SCHEDULED_ACTIVATION'),
          false,
          {
            appliedValueMapper: (name) => ({
              name,
              label: this.translateService.instant('FRONTEND.SALES_UI.SCHEDULED_ACTIVATION'),
            }),
          },
        ),
        new CheckboxFilterControl('drafted', this.translateService.instant('FRONTEND.SALES_UI.DRAFTED'), false, {
          appliedValueMapper: (name) => ({
            name,
            label: this.translateService.instant('FRONTEND.SALES_UI.DRAFTED'),
          }),
        }),
        new CheckboxFilterControl(
          'cancellation-requested',
          this.translateService.instant('FRONTEND.SALES_UI.CANCELLATION_REQUESTED'),
          false,
          {
            appliedValueMapper: (name) => ({
              name,
              label: this.translateService.instant('FRONTEND.SALES_UI.CANCELLATION_REQUESTED'),
            }),
          },
        ),
        new CheckboxFilterControl('cancelled', this.translateService.instant('FRONTEND.SALES_UI.CANCELLED'), false, {
          appliedValueMapper: (name) => ({
            name,
            label: this.translateService.instant('FRONTEND.SALES_UI.CANCELLED'),
          }),
        }),
      ])
      .addSection(this.translateService.instant('FRONTEND.SALES_UI.TAGS'), [
        new SelectFilterControl('tag', this.translateService.instant('FRONTEND.SALES_UI.INCLUDED_TAG'), tags, null, {
          appliedValueMapper: (name, value) => {
            const tagLabel = this.translateService.instant('FRONTEND.SALES_UI.TAG');
            return { name, label: `${tagLabel}: ${value}` };
          },
        }),
        new CheckboxFilterControl('untagged', this.translateService.instant('FRONTEND.SALES_UI.UNTAGGED'), false, {
          appliedValueMapper: (name) => ({
            name,
            label: this.translateService.instant('FRONTEND.SALES_UI.UNTAGGED'),
          }),
        }),
      ])
      .addSection(this.translateService.instant('FRONTEND.SALES_UI.SUBMITTED'), [
        submittedDateFromControl,
        submittedDateToControl,
      ])
      .addSection(this.translateService.instant('FRONTEND.SALES_UI.CONTRACT_START'), [
        contractStartFromControl,
        contractStartToControl,
      ])
      .addSection(this.translateService.instant('FRONTEND.SALES_UI.EXPIRY'), [
        expiryDateFromControl,
        expiryDateToControl,
      ]);

    if ((this.forcedFilterOptions.marketIds || []).length === 1) {
      this.partnerId$.pipe(take(1)).subscribe((partnerId) => {
        this.addPackageFilterSection(partnerId, this.forcedFilterOptions.marketIds[0]);
      });
    } else {
      combineLatest([this.partnerId$, this.marketFilterControl.valueChanges]).subscribe(([partnerId, market]) => {
        this.filters.removeSection(this.packageFilterSectionName);
        if (market) {
          this.addPackageFilterSection(partnerId, market.marketId);
        }
      });
    }
  }

  formatSearchAccountOption(accountGroup): string {
    const address = {
      address: accountGroup.napData.address,
      address2: accountGroup.napData.address2,
      city: accountGroup.napData.city,
      state: accountGroup.napData.state,
    };

    const formattedAddress = this.addressPipe.transform(address);
    let companyName = accountGroup.napData.companyName;
    if (formattedAddress) {
      companyName = `${companyName} - ${formattedAddress}`;
    }
    return companyName;
  }

  getMarketIds(markets: Market[]): string[] {
    let marketIds = markets.map((m) => m.marketId);
    if (this.forcedFilterOptions.marketIds) {
      marketIds = this.forcedFilterOptions.marketIds;
    }
    return marketIds;
  }

  private createSalesPeopleSelectControl(): SelectFilterControl {
    const salesPeopleOptions$: Observable<Map<string, Salesperson>> = this.salespeople$.pipe(
      map((salesPeople) => {
        salesPeople.sort((a, b) => {
          const aName = a.name.toLowerCase(),
            bName = b.name.toLowerCase();
          return aName < bName ? -1 : aName > bName ? 1 : 0;
        });
        return salesPeople;
      }),
      map((salesPeople) => {
        return salesPeople.reduce((objMap, sp) => {
          objMap.set(sp.name, sp);
          return objMap;
        }, new Map<string, Salesperson>());
      }),
      catchError((e) => {
        this.alertService.openErrorSnack('FRONTEND.SALES_UI.ERROR_LOADING_SALESPEOPLE');
        return throwError(() => e);
      }),
    );
    return new SelectFilterControl(
      'salesPeople',
      this.translateService.instant('FRONTEND.SALES_UI.SALESPERSON'),
      salesPeopleOptions$,
      null,
      {
        appliedValueMapper: (name, value) => {
          const salespersonLabel = this.translateService.instant('FRONTEND.SALES_UI.SALESPERSON');
          return { name, label: `${salespersonLabel}: ${value.name}` };
        },
      },
    );
  }

  private createSalesTeamsSelectControl(): SelectFilterControl {
    const salesTeamOptions$: Observable<Map<string, Salesteam>> = this.salesTeams$.pipe(
      take(1),
      map((salesTeams) => {
        if (!salesTeams) {
          return [];
        }

        salesTeams.sort((a, b) => {
          const aName = a.teamName.toLowerCase();
          const bName = b.teamName.toLowerCase();
          return aName < bName ? -1 : aName > bName ? 1 : 0;
        });
        return salesTeams;
      }),
      map((teams) => SalesOrdersTableFilterService.convertSalesTeamsToMap(teams)),
      catchError((e) => {
        this.alertService.openErrorSnack('FRONTEND.SALES_UI.ERROR_LOADING_SALES_TEAMS');
        return throwError(() => e);
      }),
    );

    return new SelectFilterControl(
      'salesTeam',
      this.translateService.instant('FRONTEND.SALES_UI.SALES_TEAM'),
      salesTeamOptions$,
      null,
      {
        appliedValueMapper: (name, value) => {
          const salesTeamLabel = this.translateService.instant('FRONTEND.SALES_UI.SALES_TEAM');
          return { name, label: `${salesTeamLabel}: ${value.teamName}` };
        },
      },
    );
  }

  private createAccountSearchControl(): SearchSelectFilterControl {
    const accountGroupProjectionFilter: ProjectionFilterInterface = { napData: true } as const;
    const accountSearchTerm$$: BehaviorSubject<string> = new BehaviorSubject<string>('');
    const accountGroupIdToCompanyName = new Map<string, string>();
    const accountOptions$: Observable<Map<string, string>> = combineLatest([
      this.partnerId$,
      this.markets$,
      accountSearchTerm$$,
    ]).pipe(
      switchMap(([partnerId, markets, searchTerm]) =>
        this.accountGroupService.lookup({
          filters: {
            partnerId: partnerId,
            marketIds: this.getMarketIds(markets),
          },
          projectionFilter: accountGroupProjectionFilter,
          searchTerm: searchTerm,
          pageSize: 25,
        }),
      ),
      map((response) => {
        const accountGroupLabelToId = new Map<string, string>();
        response?.accountGroups?.forEach((accountGroup) => {
          const selectOptionLabel = this.formatSearchAccountOption(accountGroup);
          accountGroupLabelToId.set(selectOptionLabel, accountGroup.accountGroupId);
          accountGroupIdToCompanyName.set(accountGroup.accountGroupId, accountGroup.napData.companyName);
        });
        return accountGroupLabelToId;
      }),
    );
    const accountLabel = this.translateService.instant('FRONTEND.SALES_UI.ACCOUNT');
    const accountSearchControl = new SearchSelectFilterControl('businessId', accountLabel, accountOptions$, null, {
      searchDisplay: (accountId) => accountGroupIdToCompanyName.get(accountId) || accountId,
      appliedValueMapper: (name, value) => {
        if (accountGroupIdToCompanyName.get(value)) {
          return { name, label: `${accountLabel}: ${accountGroupIdToCompanyName.get(value)}` };
        }
        return { name, label: `${accountLabel}: ${value}` };
      },
    });
    accountSearchControl.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((searchTerm) => {
      accountSearchTerm$$.next(searchTerm);
    });
    return accountSearchControl;
  }

  private addPackageFilterSection(partnerId: string, marketId: string): void {
    this.filters.addSection(this.packageFilterSectionName, [
      new SelectFilterControl(
        'package',
        this.translateService.instant('FRONTEND.SALES_UI.PACKAGE'),
        this.packageService.list(partnerId, [Status.PUBLISHED], marketId).pipe(
          map((resp) => {
            const packages = resp.packages || [];
            packages.sort((a, b) => {
              const aName = a.name.toLowerCase(),
                bName = b.name.toLowerCase();
              return aName < bName ? -1 : aName > bName ? 1 : 0;
            });
            return packages;
          }),
          map((sortedPackages) => {
            const values = new Map<string, Package>();
            (sortedPackages || []).map((p) => values.set(p.name, p));
            return values;
          }),
        ),
        null,
        {
          appliedValueMapper: (name, value) => {
            const marketLabel = this.translateService.instant('FRONTEND.SALES_UI.PACKAGE');
            return { name, label: `${marketLabel}: ${value.name}` };
          },
        },
      ),
    ]);
  }
}
